import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "./Reveal";

/**
 * ClosingCTA — final CTA section for a page.
 *
 * Variants:
 *  - "dark"  (default): charcoal background, used where the page's own
 *                       final content already provides a warm section
 *                       above (Home, About, Executive Brief, etc.).
 *  - "light" : warm off-white background, used on pages whose previous
 *              section is dark so the CTA does not visually merge into
 *              the footer (Operational Clarity, How BOS Works,
 *              BOS Reveal).
 */
export const ClosingCTA = ({
  title = "What execution problem keeps coming back?",
  copy = "You do not need to diagnose it before getting in touch.",
  primaryLabel = "Discuss the problem",
  primaryTo = "/contact",
  showDiscussLink = true,
  variant = "dark",
}) => {
  const isLight = variant === "light";
  return (
    <section
      data-testid="closing-cta"
      data-variant={variant}
      className={
        isLight
          ? "bg-paper text-ink border-t border-rule"
          : "bg-ink text-paper"
      }
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <FadeUp>
          <h2 className="mt-0 max-w-3xl font-display text-3xl md:text-5xl tracking-tight leading-tight">
            {title}
          </h2>
          <p
            className={`mt-6 max-w-xl text-base leading-relaxed ${
              isLight ? "text-mute" : "text-paper/60"
            }`}
          >
            {copy}
          </p>
          <div className="mt-10">
            <Link
              to={primaryTo}
              data-testid="cta-discuss-problem"
              className={`inline-flex items-center gap-2 bg-terra text-white text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 transition-colors duration-300 ${
                isLight
                  ? "hover:bg-ink hover:text-paper"
                  : "hover:bg-paper hover:text-ink"
              }`}
            >
              {primaryLabel} <ArrowUpRight size={15} />
            </Link>
          </div>
          {showDiscussLink && (
            <div className="mt-6">
              <Link
                to="/operational-clarity-review"
                data-testid="cta-take-review"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-terra link-rule"
              >
                Start the Review <ArrowUpRight size={13} />
              </Link>
            </div>
          )}
        </FadeUp>
      </div>
    </section>
  );
};
